import { useCallback, useRef } from 'react';

/**
 * Cloudflare Turnstile: the "are you human" check on public forms.
 *
 * The backend refuses a guarded request without a fresh token in
 * `x-turnstile-token` (Relay_Backend turnstile.guard.ts). Tokens are single use
 * and last five minutes; the widget renews an expired one on its own.
 *
 * The widget shows in the container the form renders, usually as a small
 * "Success" box that needed no click.
 */

/** Header the backend reads the token from. */
export const TURNSTILE_HEADER = 'x-turnstile-token';

const SCRIPT_URL =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

/** How long a visitor gets to answer an interactive challenge. */
const TOKEN_TIMEOUT_MS = 120_000;

const FAILED_MESSAGE = 'We could not confirm you are human. Please try again.';

/** The bot check could not produce a token. `message` is fit to show visitors. */
export class TurnstileError extends Error {
  constructor() {
    super(FAILED_MESSAGE);
    this.name = 'TurnstileError';
  }
}

/**
 * The two messages the backend's turnstile.guard.ts refuses with: no token
 * sent, and a token Cloudflare rejected. The guard sends no code of its own,
 * only Nest's ForbiddenException body, so the message is what tells its 403
 * from any other. Change these with the guard.
 */
const GUARD_MESSAGE = /confirm you are human/i;

/**
 * True when a request failed the bot check: no token could be fetched, or the
 * backend's Turnstile guard refused the token we sent.
 *
 * Not any 403. One refused photo upload used to read as a failed bot check,
 * and the booking was abandoned with "could not confirm you are human".
 */
export function isTurnstileFailure(error: unknown): boolean {
  if (error instanceof TurnstileError) return true;
  const response = (
    error as {
      response?: { status?: number; data?: { message?: unknown } };
    } | null
  )?.response;
  const message = response?.data?.message;
  return (
    response?.status === 403 &&
    typeof message === 'string' &&
    GUARD_MESSAGE.test(message)
  );
}

interface TurnstileApi {
  render: (el: HTMLElement, options: Record<string, unknown>) => string;
  execute: (widgetId: string) => void;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let siteKey: string | undefined;

/**
 * Set once by each app's entry point from its own env
 * (VITE_TURNSTILE_SITE_KEY). This package is compiled outside Vite too, so it
 * cannot read import.meta.env itself. No key means no check: getToken resolves
 * undefined and the request goes out without the header.
 */
export function setTurnstileSiteKey(key: string | undefined) {
  siteKey = key || undefined;
}

/**
 * Hosts on the "Relay Tow forms" widget in Cloudflare. Must match that list
 * and PROTECTED_HOSTS in the backend's turnstile.guard.ts. A listed host also
 * covers its subdomains, as in Cloudflare.
 *
 * Anywhere else (a customer's own domain, a Vercel PR preview) the widget
 * would fail, so there is no box and the backend does not ask for a token.
 */
const PROTECTED_HOSTS = [
  'relaytow.com',
  'relay-towing-app-staging.vercel.app',
  'relay-towing-app-public-website-git-staging-relay-tow-app.vercel.app',
  'localhost',
  '127.0.0.1',
];

export function isTurnstileHost(hostname: string): boolean {
  const host = hostname.toLowerCase();
  return PROTECTED_HOSTS.some(
    (allowed) => host === allowed || host.endsWith(`.${allowed}`)
  );
}

/** The site key, or undefined when the check is off for this page. */
function activeSiteKey(): string | undefined {
  if (!siteKey || typeof window === 'undefined') return undefined;
  return isTurnstileHost(window.location.hostname) ? siteKey : undefined;
}

let scriptPromise: Promise<TurnstileApi> | null = null;

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = SCRIPT_URL;
      script.async = true;
      script.onload = () =>
        window.turnstile
          ? resolve(window.turnstile)
          : reject(new TurnstileError());
      script.onerror = () => {
        // Let a later form try again rather than caching the failure.
        scriptPromise = null;
        reject(new TurnstileError());
      };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
}

/** Spread into a request's headers. Empty when the check is off. */
export function turnstileHeaders(
  token: string | undefined
): Record<string, string> {
  return token ? { [TURNSTILE_HEADER]: token } : {};
}

/**
 * Attach `containerRef` to an empty element near the submit button, then
 * `await getToken()` before each guarded request.
 *
 * The widget is always on screen and solves itself when the form opens, so
 * the token is usually ready by the time the visitor submits. Each token is
 * handed out once and the widget is reset straight after, which fetches the
 * next one. Calls are queued, so a form that uploads three photos in parallel
 * still gets three distinct tokens.
 */
export function useTurnstile() {
  const widget = useRef<Promise<{ api: TurnstileApi; id: string }> | null>(
    null
  );
  const unmount = useRef<(() => void) | null>(null);
  // A solved token nobody has taken yet.
  const ready = useRef<string | null>(null);
  // A getToken call waiting for the widget to solve.
  const waiting = useRef<{
    resolve: (token: string) => void;
    reject: (error: Error) => void;
  } | null>(null);
  const queue = useRef<Promise<unknown>>(Promise.resolve());

  // A callback ref rather than an effect: a view that swaps screens (the
  // password reset form, after a send) remounts the container, and the widget
  // has to follow it to the new element. React calls it with null on unmount,
  // which removes the widget. An effect cleanup must not do that too: dev
  // StrictMode runs cleanups on a live component, which removed the widget
  // and left the form with no box and a check that always failed.
  const containerRef = useCallback((el: HTMLDivElement | null) => {
    unmount.current?.();
    unmount.current = null;
    widget.current = null;
    ready.current = null;
    const key = activeSiteKey();
    if (!key || !el) return;

    let removed = false;
    let widgetId: string | undefined;
    const rendered = loadTurnstile().then((api) => {
      if (removed) throw new TurnstileError();
      widgetId = api.render(el, {
        sitekey: key,
        callback: (token: string) => {
          if (waiting.current) waiting.current.resolve(token);
          else ready.current = token;
        },
        'expired-callback': () => {
          ready.current = null;
        },
        'error-callback': () => {
          ready.current = null;
          waiting.current?.reject(new TurnstileError());
        },
      });
      return { api, id: widgetId };
    });
    // Nothing awaits this until the first submit; keep a load failure from
    // surfacing as an unhandled rejection before then.
    rendered.catch(() => {});
    widget.current = rendered;
    unmount.current = () => {
      removed = true;
      // A submit still waiting on this widget would otherwise hang until the
      // timeout, or take a token from the next widget and reset this one.
      waiting.current?.reject(new TurnstileError());
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
    };
  }, []);

  const getToken = useCallback((): Promise<string | undefined> => {
    if (!activeSiteKey()) return Promise.resolve(undefined);

    const next = queue.current.then(async () => {
      if (!widget.current) throw new TurnstileError();
      const { api, id } = await widget.current;
      let token = ready.current;
      ready.current = null;
      if (!token) {
        token = await new Promise<string>((resolve, reject) => {
          const timer = setTimeout(
            () => reject(new TurnstileError()),
            TOKEN_TIMEOUT_MS
          );
          waiting.current = {
            resolve: (t) => {
              clearTimeout(timer);
              resolve(t);
            },
            reject: (error) => {
              clearTimeout(timer);
              reject(error);
            },
          };
        }).finally(() => {
          waiting.current = null;
        });
      }
      // Spent once sent; start solving the next one now.
      api.reset(id);
      return token;
    });
    // A failed token must not block the tokens queued behind it.
    queue.current = next.catch(() => {});
    return next;
  }, []);

  return { containerRef, getToken };
}
